import { listTransactions, type FeedTransaction, type CategoryListItem } from '../api-client';
import { navigate } from '../router';

export async function renderFeed(root: HTMLElement, categories: CategoryListItem[]) {
  root.innerHTML = '<div class="muted">Loading…</div>';
  try {
    const { transactions } = await listTransactions();
    if (transactions.length === 0) {
      root.innerHTML = '';  // caller renders empty state
      return false;
    }
    const { html, ids } = groupAndRender(transactions, categories);
    root.innerHTML = html;
    ids.forEach(({ id, el }) => {
      const row = root.querySelector<HTMLElement>(`[data-tx-id="${el}"]`);
      if (row) {
        row.style.cursor = 'pointer';
        row.onclick = () => { void navigate(`/transactions/${id}`); };
      }
    });
    return true;
  } catch (e) {
    root.innerHTML = `<div class="muted">Couldn't load feed: ${(e as Error).message}</div>`;
    return true;
  }
}

function groupAndRender(txs: FeedTransaction[], categories: CategoryListItem[]): { html: string; ids: { id: string; el: string }[] } {
  const groups = new Map<string, FeedTransaction[]>();
  for (const t of txs) {
    const label = dateLabel(t.occurredAt);
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label)!.push(t);
  }
  const html: string[] = [];
  const ids: { id: string; el: string }[] = [];
  for (const [label, rows] of groups) {
    html.push(`<div class="feed-group-label">${label}</div>`);
    for (const t of rows) {
      html.push(txRowHtml(t, categories));
      ids.push({ id: t.id, el: t.id });
    }
  }
  return { html: html.join(''), ids };
}

function dateLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (sameDay(d, now)) return 'Today';
  const y = new Date(now.getTime() - 86400000);
  if (sameDay(d, y)) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

function txRowHtml(t: FeedTransaction, categories: CategoryListItem[]): string {
  const amount = t.amountAed.toLocaleString('en-GB', { maximumFractionDigits: 0 });
  const currencyPrefix = t.currencyOriginal !== 'AED'
    ? `<span class="tx-original">${t.amountOriginal} ${t.currencyOriginal}</span>`
    : '';
  const dot = t.reviewStatus === 'needs_review' ? '<span class="tx-dot" title="AI was unsure"></span>' : '';
  const isIncome = t.type === 'income';
  const refundTag = t.refundOfTransactionId !== null ? '<span class="tx-refund-tag">Refund</span>' : '';
  const incomeTag = isIncome ? '<span class="tx-income-tag">Income</span>' : '';
  const catLabel = formatCategoryLabel(t.categoryId, t.subcategoryId, categories);
  const name = isIncome
    ? (t.merchant ?? catLabel)
    : (t.merchant ?? t.description ?? '—');
  const amtClass = isIncome ? 'tx-amt tx-amt-income' : 'tx-amt';
  const amtPrefix = isIncome ? '+' : '';
  return `
    <div class="tx-row" data-tx-id="${escapeHtml(t.id)}">
      ${dot}
      <div class="tx-body">
        <div class="tx-name">${incomeTag}${refundTag}${escapeHtml(name)}</div>
        <div class="tx-cat">${escapeHtml(catLabel)}</div>
      </div>
      <div class="${amtClass}">${currencyPrefix}${amtPrefix}${amount}</div>
    </div>
  `;
}

function formatCategoryLabel(
  categoryId: string,
  subcategoryId: string | null,
  categories: CategoryListItem[],
): string {
  const cat = categories.find((c) => c.id === categoryId);
  if (!cat) return categoryId;
  if (!subcategoryId) return cat.name;
  const sub = categories.find((c) => c.id === subcategoryId);
  return sub ? `${cat.name} / ${sub.name}` : cat.name;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
